import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchProductByHandle, CartItem, ShopifyProduct, isShakatahEnabled } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { useShakatahCartStore } from "@/stores/shakatahCartStore";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ShoppingCart, Minus, Plus, Users } from "lucide-react";
import { formatINR } from "@/lib/currency";

const ProductDetail = () => {
  const navigate = useNavigate();
  const { handle } = useParams<{ handle: string }>();
  const [product, setProduct] = useState<ShopifyProduct["node"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  
  const addToRegularCart = useCartStore((state) => state.addItem);
  const addToShakatahCart = useShakatahCartStore((state) => state.addItem);

  useEffect(() => {
    const loadProduct = async () => {
      if (!handle) return;
      
      try {
        const data = await fetchProductByHandle(handle);
        setProduct(data);
        if (data?.variants.edges[0]) {
          setSelectedVariant(data.variants.edges[0].node.id);
        }
      } catch (err) {
        console.error("Failed to load product:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [handle]);

  const currentVariant = product?.variants.edges.find(
    (v) => v.node.id === selectedVariant
  )?.node;

  // Memoize Shakatah check to prevent recalculation on every render
  const showShakatah = useMemo(() => {
    return product ? isShakatahEnabled(product) : false;
  }, [product]);


  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Product not found</h1>
          <Link to="/" className="text-primary hover:underline">
            Return to home
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images.edges;
  const hasMultipleVariants = product.variants.edges.length > 1;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-8">
        <Link 
          to="/" 
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-secondary rounded-lg overflow-hidden">
              {images[selectedImage] ? (
                <img
                  src={images[selectedImage].node.url}
                  alt={images[selectedImage].node.altText || product.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  No Image
                </div>
              )}
            </div>
            
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-md overflow-hidden flex-shrink-0 border-2 transition-colors ${
                      selectedImage === idx ? "border-primary" : "border-transparent"
                    }`}
                  >
                    <img
                      src={img.node.url}
                      alt={img.node.altText || `${product.title} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {product.title}
              </h1>
              <p className="text-2xl font-semibold text-foreground">
                {formatINR(currentVariant?.price.amount || "0")}
              </p>
            </div>

            {product.description && (
              <p className="text-muted-foreground leading-relaxed">
                {product.description}
              </p>
            )}

            {/* Variant Selection */}
            {hasMultipleVariants && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  {product.options[0]?.name || "Option"}
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.variants.edges.map((variant) => (
                    <button
                      key={variant.node.id}
                      onClick={() => setSelectedVariant(variant.node.id)}
                      disabled={!variant.node.availableForSale}
                      className={`px-4 py-2 rounded-md border text-sm transition-colors ${
                        selectedVariant === variant.node.id
                          ? "border-primary bg-primary text-primary-foreground"
                          : variant.node.availableForSale
                          ? "border-border hover:border-primary"
                          : "border-border opacity-50 cursor-not-allowed line-through"
                      }`}
                    >
                      {variant.node.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Quantity</label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-12 text-center text-lg font-medium text-foreground">
                  {quantity}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart Buttons */}
            <div className="space-y-3">
              {/* Regular Add to Cart */}
              <Button
                onClick={() => {
                  if (!product || !currentVariant) return;
                  const cartItem: CartItem = {
                    product: { node: product },
                    variantId: currentVariant.id,
                    variantTitle: currentVariant.title,
                    price: currentVariant.price,
                    quantity,
                    selectedOptions: currentVariant.selectedOptions || [],
                  };
                  addToRegularCart(cartItem);
                  toast.success(`${product.title} added to cart`, {
                    position: "top-center"
                  });
                }}
                size="lg"
                className="w-full"
                variant="outline"
                disabled={!currentVariant?.availableForSale}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {currentVariant?.availableForSale ? "Add to Cart" : "Sold Out"}
              </Button>

              {/* Split Payment Button - only shown when Shakatah is enabled AND variant is available */}
              {showShakatah && currentVariant?.availableForSale && (
                <Button
                  onClick={() => {
                    if (!product || !currentVariant || !currentVariant.availableForSale) {
                      toast.error("This item is sold out");
                      return;
                    }
                    const cartItem: CartItem = {
                      product: { node: product },
                      variantId: currentVariant.id,
                      variantTitle: currentVariant.title,
                      price: currentVariant.price,
                      quantity,
                      selectedOptions: currentVariant.selectedOptions || [],
                    };
                    addToShakatahCart(cartItem);
                    toast.success(`${product.title} added to Split Cart`, {
                      description: "Ready to split with friends!",
                      position: "top-center",
                      action: {
                        label: "Checkout Now",
                        onClick: () => navigate("/shakatah-checkout"),
                      },
                    });
                  }}
                  size="lg"
                  className="w-full bg-gradient-to-r from-primary to-primary/70 hover:from-primary/90 hover:to-primary/60"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Add to Split Cart
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProductDetail;
